import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLazyQuery, gql } from "@apollo/client";
import { toast } from "react-toastify";
import { useParams } from "react-router";
import useGlobalMutation from "../../hooks/useGlobalMutation";
import { getCookie } from "../../utils/cookies";
import { GET_COUNTRY_SPECIALTY_LIST, CREATE_SPECIALTY, UPDATE_SPECIALTY } from "./query";
import { clearCache, updateCache } from "../../utils";
import { FormContainer, SelectInput } from "../shared";
import { proSpecialityUrl } from "../../utils/postgraphile";

const SpecialityForm = ({ formData, formType }) => {
    const { country } = useParams();
    const [fetchLoading, setFetchLoading] = useState(true);
    const [groupList, setGroupList] = useState([]);
    const [specialityList, setSpecialityList] = useState([]);
    const [specialtyName, setSpecialtyName] = useState("");

    const [getGroupData, { loading: groupLoading }] = useLazyQuery(gql(GET_COUNTRY_SPECIALTY_LIST));

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getGroupData({
                    variables: { filter: { country: { equalTo: country } } },
                });
                const groupData = response.data?.allAbbvieProGroupSpecialties?.nodes;
                if (groupData) {
                    const _groupList = groupData.map((node) => {
                        return { id: node.id, name: node.name };
                    });
                    setGroupList(_groupList);
                }
            } catch (error) {
                toast.error(error.message);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const createApiInfo = (url, data) => {
                    const token = getCookie("piap_jwt_token");
                    const apiInfo = {
                        url,
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(data),
                    };
                    return apiInfo;
                };

                const response = await fetch(
                    proSpecialityUrl,
                    createApiInfo(proSpecialityUrl, { country })
                );
                const { data } = await response.json();

                if (data && data.length > 0) {
                    const _specialityList = data.map((node) => {
                        return { id: node.Id, name: node.Name };
                    });
                    setSpecialityList(_specialityList);
                } else {
                    toast.error("No speciality found");
                }

                setFetchLoading(false);
            } catch (error) {
                toast.error(error.message);
                setFetchLoading(false);
            }
        };

        fetchData();
    }, []);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        mode: "onChange",
        defaultValues: {
            groupId: formData?.groupId || "",
            specialtyId: formData?.specialtyId || "",
        },
    });

    useEffect(() => {
        if (formType === "update") {
            reset({
                groupId: formData.groupId,
                specialtyId: formData.specialtyId,
            });
            const selectedSpecialty = specialityList.find(
                (item) => item.id === formData.specialtyId
            );

            setSpecialtyName(selectedSpecialty?.name || "");
        }
    }, [groupList, specialityList]);

    const { submitMutation, loading } = useGlobalMutation(
        formType === "update" ? UPDATE_SPECIALTY : CREATE_SPECIALTY,
        (cache, result) => {
            const newData = result?.data?.updateAbbvieProSpecialtyMapById?.abbvieProSpecialtyMap;
            if (formType === "update" && newData) {
                updateCache(cache, newData.id, newData, "AbbvieProSpecialtyMap");
            }

            if (formType !== "update") {
                clearCache(cache, "allAbbvieProGroupSpecialties");
                clearCache(cache, "allAbbvieProSpecialtyMaps");
            }
        }
    );

    const handleSpecialtyChange = (e) => {
        const selectedSpecialtyId = e.target.value;
        const selectedSpecialty = specialityList.find((item) => item.id === selectedSpecialtyId);

        setSpecialtyName(selectedSpecialty?.name || "");
    };

    const handleSubmission = async (data) => {
        const { groupId, specialtyId } = data;
        const variables =
            formType === "update"
                ? {
                      id: formData.id,
                      abbvieProSpecialtyMap: {
                          specialtyName,
                          specialtyId,
                          groupId,
                      },
                  }
                : {
                      abbvieProSpecialtyMap: {
                          specialtyName,
                          specialtyId,
                          groupId,
                      },
                  };

        const dataPath =
            formType === "update"
                ? "updateAbbvieProSpecialtyMapById.abbvieProSpecialtyMap"
                : "createAbbvieProSpecialtyMap.abbvieProSpecialtyMap";

        const isResultNull = await submitMutation(
            variables,
            dataPath,
            `Group Specialty ${formType === "update" ? "Updated" : "Created"} Successfully`
        );
        isResultNull === null && formType !== "update" && reset();
    };

    return (
        <FormContainer
            submitAction={handleSubmit(handleSubmission)}
            loading={loading || fetchLoading || groupLoading}
            submitButtonText={`${formType === "update" ? "Update" : "Create"} Group Specialty`}
            submitBtnSm={formType === "update"}
        >
            <div className="row">
                <div className="col-md-6">
                    <SelectInput
                        id="groupId"
                        labelText="Group Specialty Name"
                        options={groupList}
                        {...register("groupId", {
                            required: true,
                        })}
                        isInvalid={!!errors.groupId}
                        required
                    />
                </div>
                <div className="col-md-6">
                    <SelectInput
                        id="specialtyId"
                        labelText="Specialty Name"
                        options={specialityList}
                        {...register("specialtyId", {
                            required: true,
                            onChange: (e) => handleSpecialtyChange(e),
                        })}
                        isInvalid={!!errors.specialtyId}
                        required
                    />
                </div>
            </div>
        </FormContainer>
    );
};

export default SpecialityForm;
